import { createAsyncThunk } from '@reduxjs/toolkit';
import { CancelToken } from 'axios';
import {
  // Dialer API
  getCountriesOptionsForNumbersSearch,
  searchPhoneNumbers,
  addPhoneNumbersToCart,
  removePhoneNumberFromCart,
  getPhoneNumbersCart,
  purchasePhoneNumbersCart,
  // Manual Call Dialing API
  getContactsListForManualCallDialing,
  getDialerPhoneNumberList,
  createTwillioVoiceToken,
  initiateCall,
  endInitiatedCall,
  pickupCall,
  getMyPhoneNumberList,
  getCountryCodeOptionsListForFilters,
  getRegionOptionsListForFilters,
  getPhoneNumberLabels,
  getPhoneNumberDetails,
  assignPhoneNumber,
  bulkAssignPhoneNumber,
  bulkSetPhoneNumberLabel,
  setPhoneNumberLabel,
  updatePhoneNumberSettings,
  bulkUpdatePhoneNumberSettings,
  setPrimaryPhoneNumber,
  deletePhoneNumber,
  revokePhoneNumberRelease,
  // Voicemail Template API
  getVoicemailTemplateMinimalList,
  dropVoicemailTemplate,
  // Call Recording API
  callRecording,
  // Call Logs API
  listAllCallLogs,
  listMissedCalls,
  listIncomingVoicemails,
  // Network Diagnostic API
  getNetworkDiagnosticToken,
  deleteCallLog,
  markUnseenMissedCallCount,
  markCallAsSeen,
  getSignedUrlForCallRecording,
  getVoicemailTemplateList,
  getCallOutcomeList,
  getSignedUrlForVoicemailTemplate,
  createVoicemailTemplate,
  updateVoicemailTemplate,
  deleteVoicemailTemplate,
  getPreSignedUrl,
  callLogsListCounts,
  assignCallOutcome,
  getReportsStats,
  getReportIndividualList,
  getReportTeamwiseList,
  exportCallReports,
  exportCallLogsDetailed,
  getCallLogsProspectDrawerHeader,
  getCallLogsProspectProfileSection,
  createCallLogsNote,
  updateCallLogsNote,
  getPhoneCallMentionableUsers,
  attachProspectToCall,
} from './helpers/dialer.api';
import { ResponseErrorWithHandled, ResponseSuccess } from '../../shared/types';
import {
  AddPhoneNumbersToCartPayload,
  RemovePhoneNumberFromCartPayload,
  SearchPhoneNumbersPayload,
  PurchasePhoneNumbersCartPayload,
  GetPhoneNumbersListPayload,
  PhoneNumberListingFilters,
} from './types/phone-numbers';
import {
  GetContactsListForManualCallDialingPayload,
  GetDialerPhoneNumberListPayload,
  InitiateCallPayload,
  EndInitiatedCallPayload,
  PickupCallPayload,
  AttachProspectToCallPayload,
} from './types/manual-call-dialing';
import {
  getVoicemailTemplateMinimalListProps,
  dropVoicemailTemplatePayload,
  callRecordingPayload,
  listAllCallLogsPayload,
  GetVoicemailTemplateListProps,
  GetCallOutcomeListProps,
  UpdateVoicemailTemplateProps,
  CreateVoicemailTemplateProps,
  ReportsPayload,
  prospectCallLogsDrawerHeaderPayload,
  createCallLogsNotePayload,
  updateCallLogsNotePayload,
} from './types';

import { getTeamsAndMembersList } from '../settings/helpers/users-and-teams.api';

export const bulkUpdatePhoneNumberSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  {
    deselectedPhoneNumberIds?: number[];
    phoneNumberFilters?: PhoneNumberListingFilters;
    settings: Array<{ code: string; value: string | null }>;
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/bulkUpdatePhoneNumberSettings', async (payload, thunkAPI) => {
  try {
    return await bulkUpdatePhoneNumberSettings(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const setPrimaryPhoneNumberRequest = createAsyncThunk<
  ResponseSuccess,
  { phoneNumberId: number },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/setPrimaryPhoneNumber', async ({ phoneNumberId }, thunkAPI) => {
  try {
    return await setPrimaryPhoneNumber(phoneNumberId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get Countries Options For Numbers Search
export const getCountriesOptionsForNumbersSearchRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getCountriesOptionsForNumbersSearch', async (args, thunkAPI) => {
  try {
    return await getCountriesOptionsForNumbersSearch();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Search Phone Numbers
export const searchPhoneNumbersRequest = createAsyncThunk<
  ResponseSuccess,
  SearchPhoneNumbersPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/searchPhoneNumbers', async (args, thunkAPI) => {
  try {
    return await searchPhoneNumbers(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const addPhoneNumbersToCartRequest = createAsyncThunk<
  ResponseSuccess,
  AddPhoneNumbersToCartPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/addPhoneNumbersToCart', async (args, thunkAPI) => {
  try {
    return await addPhoneNumbersToCart(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const removePhoneNumberFromCartRequest = createAsyncThunk<
  ResponseSuccess,
  RemovePhoneNumberFromCartPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/removePhoneNumberFromCart', async (args, thunkAPI) => {
  try {
    return await removePhoneNumberFromCart(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getPhoneNumbersCartRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getPhoneNumbersCart', async (args, thunkAPI) => {
  try {
    return await getPhoneNumbersCart();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const purchasePhoneNumbersCartRequest = createAsyncThunk<
  ResponseSuccess,
  PurchasePhoneNumbersCartPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/purchasePhoneNumbersCart', async (args, thunkAPI) => {
  try {
    return await purchasePhoneNumbersCart(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get My Phone Number List
export const getMyPhoneNumberListAction = createAsyncThunk<
  ResponseSuccess,
  GetPhoneNumbersListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getMyPhoneNumberList', async (args, thunkAPI) => {
  try {
    return await getMyPhoneNumberList(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getCountryCodeOptionsListForFiltersRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getCountryCodeOptionsListForFilters', async (args, thunkAPI) => {
  try {
    return await getCountryCodeOptionsListForFilters();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Get Contacts List For Manual Call Dialing
export const getContactsListForManualCallDialingRequest = createAsyncThunk<
  ResponseSuccess,
  GetContactsListForManualCallDialingPayload & { cancelToken?: CancelToken },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getContactsListForManualCallDialing', async (args, thunkAPI) => {
  try {
    const { cancelToken, ...payload } = args;
    return await getContactsListForManualCallDialing(payload, cancelToken);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Get Dialer Phone Number List (Call Via dropdown)
export const getDialerPhoneNumberListRequest = createAsyncThunk<
  ResponseSuccess,
  GetDialerPhoneNumberListPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getDialerPhoneNumberList', async (payload, thunkAPI) => {
  try {
    return await getDialerPhoneNumberList(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Create Twillio Voice Token
export const createTwillioVoiceTokenRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/createTwillioVoiceToken', async (args, thunkAPI) => {
  try {
    return await createTwillioVoiceToken();
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Initiate Call
export const initiateCallRequest = createAsyncThunk<
  ResponseSuccess,
  InitiateCallPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/initiateCall', async (args, thunkAPI) => {
  try {
    return await initiateCall(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// End Initiated Call
export const endInitiatedCallRequest = createAsyncThunk<
  ResponseSuccess,
  EndInitiatedCallPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/endInitiatedCall', async (args, thunkAPI) => {
  try {
    return await endInitiatedCall(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});

// Pickup Call
export const pickupCallRequest = createAsyncThunk<
  ResponseSuccess,
  PickupCallPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/pickupCall', async (args, thunkAPI) => {
  try {
    return await pickupCall(args);
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getRegionOptionsListForFiltersRequest = createAsyncThunk<
  ResponseSuccess,
  null,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getRegionOptionsListForFilters', async (args, thunkAPI) => {
  try {
    return await getRegionOptionsListForFilters();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getUsersListRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getUsersListRequest', async (_args, thunkAPI) => {
  try {
    return await getTeamsAndMembersList();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e);
  }
});

export const getPhoneNumberLabelsRequest = createAsyncThunk<
  ResponseSuccess,
  { search?: string },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getPhoneNumberLabels', async (args, thunkAPI) => {
  try {
    return await getPhoneNumberLabels(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get Phone Number Details
export const getPhoneNumberDetailsRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getPhoneNumberDetails', async (id, thunkAPI) => {
  try {
    return await getPhoneNumberDetails(id);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Assign Phone Number
export const assignPhoneNumberRequest = createAsyncThunk<
  ResponseSuccess,
  {
    phoneNumberIds: number[];
    userId: number;
    assignedUser?: {
      id: number;
      firstName: string;
      lastName: string;
      email: string;
    };
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/assignPhoneNumber', async (payload, thunkAPI) => {
  try {
    // Extract only phoneNumberIds and userId for API call, exclude assignedUser
    const { assignedUser, ...apiPayload } = payload;
    return await assignPhoneNumber(apiPayload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Bulk Assign Phone Number
export const bulkAssignPhoneNumberRequest = createAsyncThunk<
  ResponseSuccess,
  {
    deselectedPhoneNumberIds?: number[];
    phoneNumberFilters?: PhoneNumberListingFilters;
    userId: number;
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/bulkAssignPhoneNumber', async (payload, thunkAPI) => {
  try {
    return await bulkAssignPhoneNumber(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Set Phone Number Label
export const setPhoneNumberLabelRequest = createAsyncThunk<
  ResponseSuccess,
  { phoneNumberIds: number[]; label: string },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/setPhoneNumberLabel', async (payload, thunkAPI) => {
  try {
    return await setPhoneNumberLabel(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Bulk Set Phone Number Label
export const bulkSetPhoneNumberLabelRequest = createAsyncThunk<
  ResponseSuccess,
  {
    deselectedPhoneNumberIds?: number[];
    phoneNumberFilters?: PhoneNumberListingFilters;
    label: string;
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/bulkSetPhoneNumberLabel', async (payload, thunkAPI) => {
  try {
    return await bulkSetPhoneNumberLabel(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Update Phone Number Settings
export const updatePhoneNumberSettingsRequest = createAsyncThunk<
  ResponseSuccess,
  {
    phoneNumberIds: number[];
    settings: Array<{ code: string; value: string | null }>;
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/updatePhoneNumberSettings', async (payload, thunkAPI) => {
  try {
    return await updatePhoneNumberSettings(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Delete Phone Number
export const deletePhoneNumberRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/deletePhoneNumber', async (id, thunkAPI) => {
  try {
    return await deletePhoneNumber(id);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Revoke Phone Number Release
export const revokePhoneNumberReleaseRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/revokePhoneNumberRelease', async (id, thunkAPI) => {
  try {
    return await revokePhoneNumberRelease(id);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get Voicemail Template Minimal List
export const getVoicemailTemplateMinimalListRequest = createAsyncThunk<
  ResponseSuccess,
  getVoicemailTemplateMinimalListProps,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getVoicemailTemplateMinimalList', async (args, thunkAPI) => {
  try {
    return await getVoicemailTemplateMinimalList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Drop Voicemail Template
export const dropVoicemailTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  dropVoicemailTemplatePayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/dropVoicemailTemplate', async (args, thunkAPI) => {
  try {
    return await dropVoicemailTemplate(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Call Recording
export const callRecordingRequest = createAsyncThunk<
  ResponseSuccess,
  callRecordingPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/callRecording', async (args, thunkAPI) => {
  try {
    return await callRecording(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// List all call logs
export const listAllCallLogsRequest = createAsyncThunk<
  ResponseSuccess,
  listAllCallLogsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/listAllCallLogs', async (args, thunkAPI) => {
  try {
    return await listAllCallLogs(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// List missed calls
export const listMissedCallsRequest = createAsyncThunk<
  ResponseSuccess,
  listAllCallLogsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/listMissedCalls', async (args, thunkAPI) => {
  try {
    return await listMissedCalls(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// List incoming voicemails
export const listIncomingVoicemailsRequest = createAsyncThunk<
  ResponseSuccess,
  listAllCallLogsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/listIncomingVoicemails', async (args, thunkAPI) => {
  try {
    return await listIncomingVoicemails(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get Network Diagnostic Token
export const getNetworkDiagnosticTokenRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getNetworkDiagnosticToken', async (_, thunkAPI) => {
  try {
    return await getNetworkDiagnosticToken();
  } catch (e) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Delete Call Log
export const deleteCallLogRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/deleteCallLog', async (callId, thunkAPI) => {
  try {
    return await deleteCallLog(callId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Mark Unseen Missed Call Count
export const markUnseenMissedCallCountRequest = createAsyncThunk<
  ResponseSuccess,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/markUnseenMissedCallCount', async (_, thunkAPI) => {
  try {
    return await markUnseenMissedCallCount();
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Mark call as seen
export const markCallAsSeenRequest = createAsyncThunk<
  ResponseSuccess,
  number[],
  { rejectValue: ResponseErrorWithHandled }
>('dialer/markCallAsSeen', async (callIds, thunkAPI) => {
  try {
    return await markCallAsSeen(callIds);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get signed URL for call recording
export const getSignedUrlForCallRecordingRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getSignedUrlForCallRecording', async (callId, thunkAPI) => {
  try {
    return await getSignedUrlForCallRecording(callId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get call outcome list
export const getCallOutcomeListRequest = createAsyncThunk<
  ResponseSuccess,
  GetCallOutcomeListProps,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getCallOutcomeList', async (args, thunkAPI) => {
  try {
    return await getCallOutcomeList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get voicemail template list
export const getVoicemailTemplateListRequest = createAsyncThunk<
  ResponseSuccess,
  GetVoicemailTemplateListProps,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getVoicemailTemplateList', async (args, thunkAPI) => {
  try {
    return await getVoicemailTemplateList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get signed URL for voicemail template
export const getSignedUrlForVoicemailTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getSignedUrlForVoicemailTemplate', async (templateId, thunkAPI) => {
  try {
    return await getSignedUrlForVoicemailTemplate(templateId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Create voicemail template
export const createVoicemailTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  CreateVoicemailTemplateProps,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/createVoicemailTemplate', async (args, thunkAPI) => {
  try {
    return await createVoicemailTemplate(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Update voicemail template
export const updateVoicemailTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  { templateId: number; payload: UpdateVoicemailTemplateProps },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/updateVoicemailTemplate', async (args, thunkAPI) => {
  try {
    return await updateVoicemailTemplate(args.templateId, args.payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Delete voicemail template
export const deleteVoicemailTemplateRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/deleteVoicemailTemplate', async (templateId, thunkAPI) => {
  try {
    return await deleteVoicemailTemplate(templateId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

export const getPreSignedUrlRequest = createAsyncThunk<
  ResponseSuccess,
  {
    fileName: string;
    documentType: string;
    fileType: string;
    fileSize: number;
    metadata?: Record<string, any>;
  },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getPreSignedUrl', async (payload, thunkAPI) => {
  try {
    return await getPreSignedUrl(payload);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Call logs list counts
export const callLogsListCountsRequest = createAsyncThunk<
  ResponseSuccess,
  listAllCallLogsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/callLogsListCounts', async (args, thunkAPI) => {
  try {
    return await callLogsListCounts(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Assign call outcome
export const assignCallOutcomeRequest = createAsyncThunk<
  ResponseSuccess,
  { outcomeId: number; dialerCallLogId: number },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/assignCallOutcome', async (args, thunkAPI) => {
  try {
    return await assignCallOutcome({
      outcomeId: args.outcomeId,
      dialerCallLogId: args.dialerCallLogId,
    });
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get reports stats
export const getReportsStatsRequest = createAsyncThunk<
  ResponseSuccess,
  ReportsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getReportsStats', async (args, thunkAPI) => {
  try {
    return await getReportsStats(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get report individual list
export const getReportIndividualListRequest = createAsyncThunk<
  ResponseSuccess,
  ReportsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getReportIndividualList', async (args, thunkAPI) => {
  try {
    return await getReportIndividualList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get report team list
export const getReportTeamListRequest = createAsyncThunk<
  ResponseSuccess,
  ReportsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getReportTeamwiseList', async (args, thunkAPI) => {
  try {
    return await getReportTeamwiseList(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Export call reports
export const exportCallReportsRequest = createAsyncThunk<
  ResponseSuccess,
  ReportsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/exportCallReports', async (args, thunkAPI) => {
  try {
    return await exportCallReports(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Export call logs detailed breakdown
export const exportCallLogsDetailedRequest = createAsyncThunk<
  ResponseSuccess,
  { userIds: number[]; startDate: string; endDate: string },
  { rejectValue: ResponseErrorWithHandled }
>('dialer/exportCallLogsDetailed', async (args, thunkAPI) => {
  try {
    return await exportCallLogsDetailed(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get call logs prospect drawer header
export const getCallLogsProspectDrawerHeaderRequest = createAsyncThunk<
  ResponseSuccess,
  prospectCallLogsDrawerHeaderPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getCallLogsProspectDrawerHeader', async (args, thunkAPI) => {
  try {
    return await getCallLogsProspectDrawerHeader(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get call logs prospect profile section
export const getCallLogsProspectProfileSectionRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getCallLogsProspectProfileSection', async (callId, thunkAPI) => {
  try {
    return await getCallLogsProspectProfileSection(callId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Create call logs note
export const createCallLogsNoteRequest = createAsyncThunk<
  ResponseSuccess,
  createCallLogsNotePayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/createCallLogsNote', async (args, thunkAPI) => {
  try {
    return await createCallLogsNote(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Update call logs note
export const updateCallLogsNoteRequest = createAsyncThunk<
  ResponseSuccess,
  updateCallLogsNotePayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/updateCallLogsNote', async (args, thunkAPI) => {
  try {
    return await updateCallLogsNote(args);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Get phone call mentionable users
export const getPhoneCallMentionableUsersRequest = createAsyncThunk<
  ResponseSuccess,
  number,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/getPhoneCallMentionableUsers', async (callId, thunkAPI) => {
  try {
    return await getPhoneCallMentionableUsers(callId);
  } catch (e: any) {
    return thunkAPI.rejectWithValue(e as ResponseErrorWithHandled);
  }
});

// Attach prospect to call
export const attachProspectToCallRequest = createAsyncThunk<
  ResponseSuccess,
  AttachProspectToCallPayload,
  { rejectValue: ResponseErrorWithHandled }
>('dialer/attachProspectToCall', async (payload, thunkAPI) => {
  try {
    return await attachProspectToCall(payload);
  } catch (e) {
    return thunkAPI.rejectWithValue(e);
  }
});
